import { twMerge } from '@/lib/classname/twMerge'
import { BaseSkeleton } from '@/ui/skeleton/BaseSkeleton'
import { type VariantProps, cva } from 'class-variance-authority'
import type { CSSProperties, ReactElement } from 'react'

export const badgeTints = {
  info: 'bg-info-bg text-info-fg',
  success: 'bg-success-bg text-success-fg',
  warning: 'bg-warning-bg text-warning-fg',
  error: 'bg-error-bg text-error-fg',
  default: 'bg-brand-2-50 text-brand-2-900',
  darker: 'bg-brand-2-800 text-white',
  lighter: 'bg-white text-brand-2-900',
  discount: 'bg-error-bg text-error-fg', // Currently same as error on purpose, will have its own color later
  loyalty: 'bg-loyalty-light text-loyalty-primary',
  brand: 'bg-brand-1-50 text-primary',
  'click-and-collect': 'bg-mango-orange-100 text-brand-2-900 [&_svg]:text-mango-orange-400',
  none: '', // Allows external style injection
}

export const badgeSizes = {
  md: 'h-4 text-md leading-light [&_svg]:size-[0.875rem]',
  sm: 'h-3 text-sm leading-lighter [&_svg]:size-[0.75rem]',
}

const badgeCva = cva(
  'inline-flex max-w-full flex-row items-center justify-center gap-1 overflow-hidden text-nowrap rounded-full border-px border-solid border-white px-2 py-1 font-medium hd:border-1.5 [&_svg]:shrink-0',
  {
    variants: {
      tint: badgeTints,
      size: badgeSizes,
    },
    defaultVariants: {
      tint: 'default',
      size: 'md',
    },
  },
)

type BadgeVariantProps = VariantProps<typeof badgeCva>

export type BadgeProps = UiComponentProps &
  React.PropsWithChildren & {
    /** The semantic variant of the badge */
    tint: NonNullable<BadgeVariantProps['tint']>

    /** The size of the badge */
    size: NonNullable<BadgeVariantProps['size']>

    /** The icon inside the badge */
    icon?: ReactElement

    /** Inline styles for the badge */
    style?: CSSProperties
  }

export function Badge({
  tint,
  size,
  icon,
  children,
  className,
  style,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: BadgeProps): React.ReactElement {
  return (
    <span
      id={id}
      className={twMerge(badgeCva({ tint, size }), className)}
      style={style}
      data-testid={dataTestId ?? 'badge'}
      aria-label={ariaLabel}
    >
      {!!icon && (
        <span data-testid={`${dataTestId ?? 'badge'}/icon`} className="shrink-0">
          {icon}
        </span>
      )}
      <span data-testid={`${dataTestId ?? 'badge'}/text`} className="min-w-0 truncate">
        {children}
      </span>
    </span>
  )
}

export function BadgeSkeleton({
  size = 'md',
  className,
}: {
  size?: NonNullable<BadgeVariantProps['size']>
  className?: string
}): React.ReactElement {
  return (
    <BaseSkeleton
      className={twMerge(
        badgeCva({ size }),
        'w-16 bg-[--skeleton-bg-color]',
        size === 'sm' && 'w-12',
        className,
      )}
      data-testid="badge-skeleton"
    />
  )
}
